import {Form, InputNumber, Modal, notification, } from "antd";
import BMButton, {BMButtonProps} from "../BMButton";
import React, {ReactElement, useContext, useEffect, useState} from "react";
import {useQuoteContext} from "../../contexts/QuoteContext";
import {
    dealerEditBevMaximum,
    dealerEditCustomChassisMaximum, dealerEditIceMaximum,
    defaultMaxDiscountPercent,
    MotorFamily,
    QUOTE_DEFAULTS
} from "../../api/models";
import _ from "lodash";
import {ConfiguratorContext} from "../../context";
import {FORM_PERCENT_DISCOUNT} from "../../pages/configurator";
import Utils from "../../util/util";
import BMReadOnly from "../BMReadOnly";

interface PercentDiscountFormValues {
    dealerNet?: number
    targetPrice?: number
    percentDiscount?: number
}


const PercentDiscountButtonModal = (props: Omit<BMButtonProps, 'onChange' | 'label'> & {
    label?: string | ReactElement | undefined
    onChange: (v:number) => void
}) => {

    const configurator = useContext(ConfiguratorContext);
    const { quote } = useQuoteContext();

    const {onChange, ...btnProps} = props;

    const [isOpen, setIsOpen] = useState<boolean>();
    const [initialValues, setInitialValues] = useState<PercentDiscountFormValues>();
    const [form] = Form.useForm();

    useEffect(() => {
        form.resetFields();
        //show any errors
        form.validateFields();
    }, [initialValues]);

    const selectedModel = quote?.model;
    const breakdown = quote?.pricingBreakdown!;
    const isDealer = configurator.isDealerSales();

    const hasIncentiveProgram = !!(quote?.incentivePrograms?.length);
    const getMinimumDiscountPercent = () : number => {
        if ( !hasIncentiveProgram ) return QUOTE_DEFAULTS.percentDiscount;

        const selected = quote?.incentivePrograms || [];
        const pdLst = selected.map( ip => ip.percentDiscount ).concat(QUOTE_DEFAULTS.percentDiscount);
        return _.max( pdLst ) || QUOTE_DEFAULTS.percentDiscount;
    }

    const getMaximumDiscountPercent = () => {
        if ( quote?.incentivePrograms?.length ) return defaultMaxDiscountPercent;

        if (selectedModel) {
            if ((selectedModel?.partNumberSuffix === "C" ) && isDealer) {
                return dealerEditCustomChassisMaximum;
            }
            if (quote?.motorFamily === MotorFamily.BEV && isDealer) {
                return dealerEditBevMaximum;
            }
            if (quote?.motorFamily === MotorFamily.ICE && isDealer) {
                return dealerEditIceMaximum;
            }
        }
        return defaultMaxDiscountPercent;
    }

    const minDiscountPercent = getMinimumDiscountPercent();
    const maxDiscountPercent = getMaximumDiscountPercent();
    const validateDiscount = async (_rule: any, value: string, _cb: any, enforce?: boolean) => {

        if (!enforce) {
            return true;
        }

        try {
            const n = parseFloat(value);
            if (n >= minDiscountPercent && n <= maxDiscountPercent ) {
                return true;
            }
        } catch (e) { }
        throw new Error("validation failed");
    };

/*
  const calculatePercentDiscount = (target:number):number | undefined => {
    if ( !breakdown ) return;

    const dealerPrice = breakdown.dealerPrice || 0;
    const additionalDiscount = dealerPrice - target;

    const discount = breakdown.dealerNet * breakdown.percentDiscount / 100;
    const newPercentDiscount = ( discount + additionalDiscount ) / breakdown.dealerNet * 100;
    const value = Math.round( newPercentDiscount * 10000 ) / 10000; //four decimal places precision

    return value;
  }
 */

    const calculateTargetPrice = (percentDiscount:number | undefined):number | undefined => {
        if ( !percentDiscount ) return;

        const additionalDiscount = breakdown.dealerNet * ( breakdown.percentDiscount - percentDiscount ) / 100;

        const value = breakdown.dealerPrice + additionalDiscount;

        return Math.round(value);
    }

    const calculatePercentDiscount = (targetPrice:number | undefined):number | undefined => {
        if ( !targetPrice ) return;

        const dealerPrice = breakdown.dealerPrice || 0;
        const additionalDiscount = dealerPrice - targetPrice;

        const discountPrice = breakdown.dealerNet * breakdown.percentDiscount / 100;
        const newPercentDiscount = ( discountPrice + additionalDiscount ) / breakdown.dealerNet * 100;
        const value = Math.round( newPercentDiscount * 10000 ) / 10000; //four decimal places precision

        return value;
    }

    const handleOk = async () => {

        try {
            const values = await form.validateFields() as PercentDiscountFormValues;

            if ( values.percentDiscount !== undefined ) {
                props.onChange?.(values.percentDiscount);
                setIsOpen(false);
            }
        }
        catch(validationErrors) {
            notification.error({message: "Please fix validation errors." });
        }

    }

    const resetTargetValue = () => {

        setInitialValues({
            [FORM_PERCENT_DISCOUNT]: quote?.percentDiscount || 0,
            targetPrice: initialTargetPrice,
            dealerNet: quote?.pricingBreakdown?.dealerNet,
        })
    }

    const validatePercentDiscount = async (rule: any, value: string, cb: any) => {

        const percentDiscount = Number( value );

        if ( isNaN(percentDiscount) ) {
            return false;
        }

        const target = String(percentDiscount);
        return validateDiscount( rule, target, cb, true );
    };


    const handleDisabledClick = () => {
        if ( !breakdown ) notification.warning({message:"Please wait for pricing to load."});
        props.onDisabledClick?.();
    }

    const handleValuesChange = (changedValues: PercentDiscountFormValues, values: PercentDiscountFormValues) => {

        if ( 'percentDiscount' in changedValues ) {
            const targetPrice = calculateTargetPrice(changedValues.percentDiscount);
           form.setFieldValue( 'targetPrice', targetPrice);
        }
        if ( 'targetPrice' in changedValues ) {
            const percentDiscount = calculatePercentDiscount(changedValues.targetPrice);
            form.setFieldValue( 'percentDiscount', percentDiscount);
        }
        form.validateFields();

    }

    const initialTargetPrice = quote?.poNumber?.amount || breakdown?.dealerPrice || 0;

    const btnTitle = props.label;

    return <>
        <BMButton
            data-testid={"percentDiscountBtn"}
            className="ghostBmButton"
            type="text"
            {...btnProps}
            style={{...props.style, padding: 0}}
            disabled={!breakdown || btnProps.disabled}
            onDisabledClick={handleDisabledClick}
            onClick={() => setIsOpen(true)}
        >{btnTitle}</BMButton>
        <Modal
            title={"Concession"}
            open={isOpen}
            onOk={() => handleOk()}
            onCancel={() => setIsOpen(false)}
            afterOpenChange={resetTargetValue}
        >

            <Form form={form}
                  onValuesChange={handleValuesChange}
                  initialValues={{
                      [FORM_PERCENT_DISCOUNT]: quote?.percentDiscount || 0,
                      targetPrice: initialTargetPrice,
                      dealerNet: quote?.pricingBreakdown?.dealerNet,
                  }}
            >
                <Form.Item label={"Dealer Net"} name={"dealerNet"}>
                    <BMReadOnly readOnly={true} renderValue={(v) => Utils.formatMoney(Number(v))} ><InputNumber /></BMReadOnly>
                </Form.Item>
                <Form.Item
                    label={ ( !maxDiscountPercent ) ? "Concession" : `Concession (${minDiscountPercent}-${maxDiscountPercent})` }
                    name={FORM_PERCENT_DISCOUNT}
                    rules={[
                        {
                            message: `Concession must be between ${minDiscountPercent} and ${maxDiscountPercent}`,
                            validator: validatePercentDiscount,
                        },
                    ]} >
                        <InputNumber precision={4}
                                     controls={false}
                                     stringMode
                                     formatter={(value) => value + '%'}
                                     parser={(value) => value!.replace('%', '')}
                                     style={{width:"8rem"}}
                                     />
                </Form.Item>

                <Form.Item
                    label="Target Dealer Price"
                    name="targetPrice"
                    >
                    <InputNumber
                        formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                        placeholder="Type dollar amount."
                        controls={false}
                        style={{width:"8rem"}}
                    />
                </Form.Item>
            </Form>
        </Modal>
    </>
}

export default PercentDiscountButtonModal;

